import { toast } from "react-toastify";
import ErrorToastBody from "../components/ErrorToastBody";

/**
 * Wrap Axios call with automatic toast handling
 * @param {Function} axiosCall - function returning axios promise
 * @param {string} toastId - toastId to use for loading/update
 * @param {object} tokenData - optional tokens for getLabel ({name, email, etc.})
 * @param {Function} getLabel - function to translate the labels
 * @returns {Promise<any>} - response data
 */
export const toastAxios = async (
  axiosCall,
  toastId,
  tokenData = {},
  getLabel
) => {
  // mostra il toast loading
  toast.loading(getLabel("toast_inProgress"), {
    toastId,
    type: toast.TYPE.INFO,
    position: toast.POSITION.BOTTOM_RIGHT,
    exclude: true,
  });

  try {
    const response = await axiosCall();

    // requires_confirmation: NON è un errore, è un flusso di conferma (HTTP 200).
    // Il backend non manda `success`, quindi senza questo check finirebbe nel
    // ramo error. Chiudiamo il toast di loading e lasciamo gestire al chiamante.
    if (response?.requires_confirmation === true) {
      toast.dismiss(toastId);
      return response;
    }

    // gestione success/error basata su server
    if (response?.success) {
      const successLabel = getLabel(response.success, tokenData);
      const render = successLabel.startsWith?.("#")
        ? (response.success_message || successLabel)
        : successLabel;
      toast.update(toastId, {
        render,
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 4000,
      });
    } else {
      // fallback error
      toast.update(toastId, {
        render: getLabel(response?.error || "toast_errorGeneric", tokenData),
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 5000,
      });
    }

    return response;
  } catch (e) {
    console.error(e);
    const status = e?.response?.status;
    const data = e?.response?.data;
    // reqId per il debug: preferisci il body, fallback all'header X-Request-Id
    const reqId = data?.reqId || e?.response?.headers?.["x-request-id"];
    let message = "toast_errorGeneric"; // fallback

    if (status === 401) message = getLabel("toast_unauthorized", tokenData);
    else if (status === 403) {
      if (data?.details?.resource) {
        const m = getLabel("permissions.denied_detail", { ...tokenData, resource: data.details.resource, action: data.details.action });
        message = m.startsWith("#") ? getLabel("toast_forbidden", tokenData) : m;
      } else {
        message = getLabel("toast_forbidden", tokenData);
      }
    }
    else if (status === 404) message = getLabel("toast_notFound", tokenData);
    else if (status === 422 && data?.errors) {
      message = Object.values(data.errors)
        .flat()
        .map((err) => getLabel(err, tokenData))
        .join("\n");
    } else if (data?.error) {
      const interpTokens = { ...tokenData, ...(data?.details || {}) };
      if (data?.details) {
        // prova prima la versione dettagliata con interpolazione dei placeholder
        message = getLabel(data.error + '_detail', interpTokens);
        if (message.startsWith('#')) {
          // _detail key non esiste, usa la base passando comunque i dettagli
          message = getLabel(data.error, interpTokens);
        }
      } else {
        message = getLabel(data.error, tokenData);
      }
      // Se la traduzione non viene trovata, usa error_message come fallback
      if (message.startsWith('#') && data?.error_message) {
        message = data.error_message;
      }
    }

    // testo di riferimento col reqId; fallback hardcoded se la label manca dal dizionario
    let refText = null;
    if (reqId) {
      refText = getLabel("toast_errorRef", { reqId });
      if (refText.startsWith("#")) refText = `Codice errore: ${reqId}`;
    }

    toast.update(toastId, {
      render: reqId ? (
        <ErrorToastBody message={message} refText={refText} reqId={reqId} />
      ) : (
        message
      ),
      type: toast.TYPE.ERROR,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: reqId ? 10000 : 5000,
    });

    // Optimistic-concurrency conflict (operation completion): surface a sentinel
    // (still shows the toast above) so the caller can refetch the stale entities
    // listed in details.conflicts and let the user review. Other callers ignore
    // it because it has no `success`.
    if (status === 409 && data?.error === "operations.conflict") {
      return { conflict: true, details: data?.details || null };
    }

    return null;
  }
};
